'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Ticket,
  Users,
  BarChart3,
  CreditCard,
  ArrowRight,
  Plus,
  Check,
  CalendarPlus,
  Shield,
  LineChart,
  Smartphone,
  UserCheck,
  SearchX,
  Sparkles,
  TrendingUp,
  Music,
  Laptop,
  Briefcase,
  GraduationCap,
  Trophy,
  Activity,
  Zap,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Landmark,
  Mic2,
  Lightbulb,
  Scale,
  BookOpen,
  Code2,
  FileText,
  MessageSquare,
} from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuthStore } from '@/lib/auth-store';
import { getCreateEventPath, userCanCreateEvents } from '@/lib/create-event';
import { canBecomeOrganizer, getEffectiveRole } from '@/lib/rbac';
import { resolveDashboardForUser } from '@/lib/role-dashboard';
import { eventsApi, adminApi, organizerReviewsApi, type OrganizerReview } from '@/lib/api';
import { TestimonialV2, DEFAULT_TESTIMONIALS } from '@/components/ui/testimonial-v2';
import { getPage, type Page } from '@/lib/cms';
import dynamic from 'next/dynamic';
import { HeroBanner } from '@/components/ui/HeroBanner';
import { SectionsBlock } from '@/components/cms/SectionRenderer';
import { TechHeroSection } from '@/components/home/TechHeroSection';
import { HeroWireframeGlobe } from '@/components/home/HeroWireframeGlobe';
import { getApiBaseUrl, getDashboardOrigin } from '@/lib/env';
import { resolveStorageUrl } from '@/lib/storage-url';


const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'music', label: 'Music' },
  { value: 'tech', label: 'Tech' },
  { value: 'business', label: 'Business' },
  { value: 'education', label: 'Education' },
  { value: 'sports', label: 'Sports' },
];

const HOST_FEATURES = [
  { icon: CalendarPlus, title: 'Create Events', description: 'Set up events in minutes with a simple wizard.', caption: 'Fast wizard & instant launch' },
  { icon: Ticket, title: 'Ticketing System', description: 'Sell tickets with QR codes and multiple types.', caption: 'QR ticketing & tiered passes' },
  { icon: Users, title: 'Attendee Management', description: 'Registrations, check-in, and communications.', caption: 'Live check-in & messaging' },
  { icon: LineChart, title: 'Real-Time Analytics', description: 'Track sales, revenue, and engagement.', caption: 'Live revenue & attendance stats' },
  { icon: CreditCard, title: 'Payment Processing', description: 'Secure payments and payouts.', caption: 'Secure checkout & fast payouts' },
];

const HOW_IT_WORKS = [
  { step: 1, title: 'Create Your Event', body: 'Add details, dates, venue, and ticket types.', icon: CalendarPlus, caption: 'Quick 3-minute setup flow' },
  { step: 2, title: 'Sell Tickets Online', body: 'Share your event page and start selling.', icon: Ticket, caption: 'Instant checkout & social share' },
  { step: 3, title: 'Manage Events, Attendees & Analytics', body: 'Check-in, communicate, and track performance.', icon: BarChart3, caption: 'Live scanner & real-time stats' },
];

const PLATFORM_FEATURES = [
  { icon: Shield, title: 'Secure Ticketing', description: 'QR codes and fraud protection.' },
  { icon: Calendar, title: 'Smart Event Management', description: 'Sessions, schedule, and team tools.' },
  { icon: LineChart, title: 'Advanced Analytics', description: 'Insights and reports.' },
  { icon: Smartphone, title: 'Mobile Scanning Tools', description: 'Check-in on any device.' },
  { icon: UserCheck, title: 'Automated Check-ins', description: 'Fast entry for attendees.' },
];

const DISCOVERY_HIGHLIGHTS = [
  { icon: MapPin, label: 'Location-aware', value: 'Find events in your city' },
  { icon: Calendar, label: 'Fresh Calendar', value: 'Upcoming dates at a glance' },
  { icon: Ticket, label: 'Quick Register', value: 'Join in a few taps' },
  { icon: SearchX, label: 'Smart Filters', value: 'Category-based discovery' },
  { icon: UserCheck, label: 'Any Audience', value: 'Student to pro events' },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};



function DiscoveryFeaturePill({ point }: { point: { icon: any; label: string; value: string } }) {
  const Icon = point.icon;

  return (
    <motion.div
      variants={item}
      whileHover={{ y: -4 }}
      className="group relative overflow-hidden rounded-[22px] border border-white/80 bg-bg/85 px-4 py-4 text-left shadow-[0_10px_26px_rgba(45,53,54,0.06)] backdrop-blur-sm transition-all duration-300 hover:border-landing-primary/30 hover:shadow-[0_18px_36px_rgba(45,53,54,0.12)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-landing-primary/6 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-landing-primary/15 bg-landing-primary/10 text-landing-primary transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-muted">{point.label}</p>
          <p className="mt-1 text-sm leading-6 text-landing-text/90">{point.value}</p>
        </div>
      </div>
    </motion.div>
  );
}

function DiscoveryEventCard({ event }: { event: any }) {
  const tickets = Array.isArray(event?.tickets) ? event.tickets : [];
  const hasPaidTicket = tickets.length > 0 && Number(tickets[0]?.price) > 0;
  const priceLabel = hasPaidTicket ? `From ₹${Number(tickets[0]?.price).toLocaleString()}` : 'Free entry';
  const eventHref = event?.slug ? `/events/${event.slug}` : '/events';

  return (
    <motion.div variants={item} whileHover={{ y: -8 }} className="h-full">
      <Link
        href={eventHref}
        className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/80 bg-bg/92 shadow-[0_16px_40px_rgba(45,53,54,0.08)] backdrop-blur-sm transition-all duration-300 hover:border-landing-primary/30 hover:shadow-[0_26px_60px_rgba(45,53,54,0.14)]"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white to-landing-bg/35 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
        <div className="relative h-48 overflow-hidden sm:h-52">
          {event.cover_image ? (
            <img
              src={resolveStorageUrl(event.cover_image) ?? event.cover_image}
              alt={event.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,rgba(152,170,157,0.24),rgba(242,239,226,0.94)_58%,rgba(232,226,214,0.98))]">
              <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/80 bg-bg/70 text-landing-primary shadow-[0_16px_36px_rgba(45,53,54,0.08)] backdrop-blur-sm transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105">
                <Calendar className="h-7 w-7" />
              </div>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/26 via-black/4 to-transparent" />
          {event.type && (
            <div className="absolute left-4 top-4 rounded-full border border-white/30 bg-bg/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-text shadow-[0_10px_20px_rgba(0,0,0,0.08)] backdrop-blur-md">
              {String(event.type).replace(/[_-]+/g, ' ')}
            </div>
          )}
        </div>

        <div className="relative flex flex-1 flex-col p-5 sm:p-6">
          <h3 className="line-clamp-2 text-lg font-semibold tracking-tight text-landing-text transition-colors duration-300 group-hover:text-landing-primary sm:text-xl">
            {event.title}
          </h3>

          <div className="mt-3 flex flex-wrap gap-2 text-xs text-landing-muted">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-landing-border/80 bg-landing-bg/60 px-3 py-1.5">
              <Calendar className="h-3.5 w-3.5 text-landing-primary" />
              {event.start_date ? toReadableDate(event.start_date) : 'TBA'}
            </span>
            {event.venue_city && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-landing-border/80 bg-landing-bg/60 px-3 py-1.5">
                <MapPin className="h-3.5 w-3.5 text-landing-primary" />
                <span className="line-clamp-1">{event.venue_city}</span>
              </span>
            )}
          </div>

          <p className="mt-4 line-clamp-2 text-sm leading-6 text-landing-muted">
            Discover a curated experience with smooth registration, live scheduling, and attendee-ready details.
          </p>

          <div className="mt-5 flex items-end justify-between gap-3 border-t border-landing-border/70 pt-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-muted">Starting at</p>
              <p className="mt-1 text-base font-semibold tracking-tight text-landing-text">{priceLabel}</p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-landing-primary px-4 py-2 text-xs font-semibold text-text-inverse shadow-[0_12px_24px_rgba(105,124,112,0.24)] transition-all duration-300 group-hover:bg-landing-primaryHover group-hover:shadow-[0_18px_34px_rgba(105,124,112,0.28)]">
              Attend
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function DiscoveryLoadingCard() {
  return (
    <div className="overflow-hidden rounded-[28px] border border-white/80 bg-bg/90 shadow-[0_16px_40px_rgba(45,53,54,0.08)]">
      <div className="h-48 animate-pulse bg-gradient-to-br from-landing-border/70 to-landing-primary/10 sm:h-52" />
      <div className="space-y-4 p-5 sm:p-6">
        <div className="h-5 w-3/4 animate-pulse rounded-full bg-landing-border/70" />
        <div className="flex gap-2">
          <div className="h-8 w-28 animate-pulse rounded-full bg-landing-border/60" />
          <div className="h-8 w-24 animate-pulse rounded-full bg-landing-border/60" />
        </div>
        <div className="space-y-2">
          <div className="h-3.5 w-full animate-pulse rounded-full bg-landing-border/60" />
          <div className="h-3.5 w-4/5 animate-pulse rounded-full bg-landing-border/60" />
        </div>
        <div className="flex items-center justify-between border-t border-landing-border/70 pt-4">
          <div className="h-4 w-24 animate-pulse rounded-full bg-landing-border/70" />
          <div className="h-9 w-24 animate-pulse rounded-full bg-landing-border/70" />
        </div>
      </div>
    </div>
  );
}

function HostFeatureCard({ feature }: { feature: { icon: any; title: string; description: string; caption?: string } }) {
  const Icon = feature.icon;

  return (
    <motion.div
      variants={item}
      whileHover={{ y: -8 }}
      className="group relative flex h-full overflow-hidden rounded-[30px] border border-white/80 bg-bg/90 p-5 shadow-[0_16px_40px_rgba(45,53,54,0.08)] backdrop-blur-sm transition-all duration-300 hover:border-landing-primary/30 hover:shadow-[0_24px_56px_rgba(45,53,54,0.14)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-landing-primary/6 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-landing-primary/10 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex h-full flex-col">
        <div className="flex items-center justify-between gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[18px] border border-landing-primary/15 bg-landing-primary/10 text-landing-primary shadow-[0_12px_24px_rgba(152,170,157,0.12)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105">
            <Icon className="h-6 w-6" />
          </div>
          <span className="rounded-full border border-landing-border/80 bg-landing-bg/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-landing-muted">
            Toolkit
          </span>
        </div>

        <h3 className="mt-4 text-lg font-semibold tracking-tight text-landing-text transition-colors duration-300 group-hover:text-landing-primary">
          {feature.title}
        </h3>
        <p className="mt-2 text-sm leading-7 text-landing-muted">
          {feature.description}
        </p>

        <div className="mt-auto pt-6">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-landing-border to-transparent" />
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.2em] text-landing-muted/80">
            {feature.caption || 'Built for faster event ops'}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function HowItWorksStepCard({ step, index }: { step: { step: number; title: string; body: string; icon: any; caption?: string }; index: number }) {
  const Icon = step.icon;

  return (
    <motion.article
      variants={item}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex h-full overflow-hidden rounded-[30px] border border-white/80 bg-bg/90 p-6 text-left shadow-[0_16px_40px_rgba(45,53,54,0.08)] backdrop-blur-sm transition-all duration-300 hover:border-landing-primary/30 hover:shadow-[0_24px_56px_rgba(45,53,54,0.14)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-landing-primary/6 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-landing-primary/10 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex h-full w-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-[18px] border border-landing-primary/15 bg-landing-primary/10 text-landing-primary shadow-[0_12px_24px_rgba(152,170,157,0.12)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105">
              <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
            </span>
            <span className="inline-flex items-center rounded-full border border-landing-border/80 bg-landing-bg/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-muted">
              Step {step.step}
            </span>
          </div>
          <div className="hidden h-px flex-1 self-center bg-gradient-to-r from-landing-primary/0 via-landing-primary/35 to-landing-primary/0 md:block" />
          <span className="hidden rounded-full border border-landing-primary/15 bg-landing-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-landing-primary md:inline-flex">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>

        <div className="mt-5">
          <h3 className="text-xl font-semibold tracking-tight text-landing-text transition-colors duration-300 group-hover:text-landing-primary">
            {step.title}
          </h3>
          <p className="mt-3 text-sm leading-7 text-landing-muted">
            {step.body}
          </p>
        </div>

        <div className="mt-auto pt-6">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-landing-border to-transparent" />
          <p className="mt-4 text-xs font-medium uppercase tracking-[0.2em] text-landing-muted/80">
            {step.caption || 'Designed to reduce setup time'}
          </p>
        </div>
      </div>
    </motion.article>
  );
}

function PlatformFeatureCard({ feature }: { feature: { icon: any; title: string; description: string } }) {
  const Icon = feature.icon;

  return (
    <motion.article
      variants={item}
      whileHover={{ y: -8 }}
      className="group relative flex h-full overflow-hidden rounded-[30px] border border-white/80 bg-bg/90 p-5 text-left shadow-[0_16px_40px_rgba(45,53,54,0.08)] backdrop-blur-sm transition-all duration-300 hover:border-landing-primary/30 hover:shadow-[0_24px_56px_rgba(45,53,54,0.14)]"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-landing-primary/6 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-landing-primary/10 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex h-full w-full flex-col items-start">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[18px] border border-landing-primary/15 bg-landing-primary/10 text-landing-primary shadow-[0_12px_24px_rgba(152,170,157,0.12)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-105">
            <Icon className="h-6 w-6 transition-transform duration-300 group-hover:scale-110" />
          </div>
          <span className="rounded-full border border-landing-border/80 bg-landing-bg/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-landing-muted">
            Core feature
          </span>
        </div>

        <h3 className="mt-4 text-lg font-semibold tracking-tight text-landing-text transition-colors duration-300 group-hover:text-landing-primary">
          {feature.title}
        </h3>
        <p className="mt-2 text-sm leading-7 text-landing-muted">
          {feature.description}
        </p>

        <div className="mt-auto w-full pt-6">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-landing-border to-transparent" />
        </div>
      </div>
    </motion.article>
  );
}

function toReadableDate(input?: string | null): string {
  if (!input) return 'TBA';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return 'TBA';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function toTitleCase(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


const SECTION_WIDTH_CLASS = 'mx-auto w-full max-w-[76rem]';
const LANDING_CARD_CLASS = 'landing-box-hover overflow-hidden rounded-[32px] border border-white/40 bg-bg/30 backdrop-blur-md shadow-[0_14px_40px_rgba(45,53,54,0.06)]';
const LANDING_SECTION_SHELL_CLASS =
  'mx-auto w-full max-w-[76rem] rounded-[32px] border border-white/50 bg-bg/25 px-5 py-7 shadow-[0_24px_54px_rgba(45,53,54,0.04)] backdrop-blur-md sm:px-8 lg:px-10 lg:py-10 transition-all duration-300';
const HOME_PLACEHOLDER_SECTION_KEYS = new Set(['hero_title', 'hero_subtitle', 'features_intro', 'stats_heading']);

type DashboardPreviewEvent = {
  id?: string;
  title?: string;
  status?: string;
  start_date?: string;
};

type PublicStatsPayload = {
  total_events?: number;
  total_tickets_sold?: number;
  total_organizers?: number;
  active_cities?: number;
};

function formatPublicStat(value: number | undefined | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

type TimeframeKey = '7d' | '30d' | '12m';

const TIMEFRAME_CONFIG: Record<
  TimeframeKey,
  {
    label: string;
    sublabel: string;
    multiplier: number;
    growth: string;
    points: number[];
    labels: string[];
    peakRate: string;
    conversion: string;
    avgScan: string;
  }
> = {
  '7d': {
    label: 'Last 7 Days',
    sublabel: 'Weekly registration momentum',
    multiplier: 1,
    growth: '+28.4%',
    points: [16, 24, 19, 32, 28, 44, 52],
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    peakRate: '52 tickets/day',
    conversion: '96.4%',
    avgScan: '1.8s scan',
  },
  '30d': {
    label: 'Last 30 Days',
    sublabel: 'Monthly sales trajectory',
    multiplier: 3.8,
    growth: '+41.2%',
    points: [24, 38, 45, 52, 68, 78, 96],
    labels: ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'],
    peakRate: '96 tickets/day',
    conversion: '95.1%',
    avgScan: '1.6s scan',
  },
  '12m': {
    label: 'Past Year',
    sublabel: 'Annual platform scale',
    multiplier: 24,
    growth: '+118%',
    points: [110, 160, 220, 310, 420, 510, 680],
    labels: ['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov', 'Dec'],
    peakRate: '680 tickets/mo',
    conversion: '97.4%',
    avgScan: '1.4s scan',
  },
};

function DashboardPreviewMockup({
  stats,
  recentEvents,
  loading,
}: {
  stats?: PublicStatsPayload | null;
  recentEvents: DashboardPreviewEvent[];
  loading?: boolean;
}) {
  const [timeframe, setTimeframe] = useState<TimeframeKey>('7d');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const baseTickets = stats?.total_tickets_sold ?? 121;
  const currentConfig = TIMEFRAME_CONFIG[timeframe];
  const displayTickets = Math.round(baseTickets * currentConfig.multiplier);

  // SVG Chart Geometry
  const width = 460;
  const height = 150;
  const padX = 25;
  const padY = 20;

  const points = currentConfig.points;
  const minVal = Math.min(...points) * 0.75;
  const maxVal = Math.max(...points) * 1.15;

  const coords = useMemo(() => {
    return points.map((val, idx) => {
      const x = padX + (idx * (width - 2 * padX)) / (points.length - 1);
      const y = height - padY - ((val - minVal) / (maxVal - minVal)) * (height - 2 * padY);
      return { x, y, val };
    });
  }, [points, minVal, maxVal]);

  // Smooth SVG path using Catmull-Rom or cubic Bezier
  const { linePath, areaPath } = useMemo(() => {
    if (coords.length === 0) return { linePath: '', areaPath: '' };

    let d = `M ${coords[0].x} ${coords[0].y}`;
    for (let i = 0; i < coords.length - 1; i++) {
      const p0 = coords[i];
      const p1 = coords[i + 1];
      const cpX = (p0.x + p1.x) / 2;
      d += ` C ${cpX} ${p0.y}, ${cpX} ${p1.y}, ${p1.x} ${p1.y}`;
    }

    const area = `${d} L ${coords[coords.length - 1].x} ${height} L ${coords[0].x} ${height} Z`;
    return { linePath: d, areaPath: area };
  }, [coords]);

  const fallbackEvents: DashboardPreviewEvent[] = [
    { id: '1', title: 'HackPaglu 2026', status: 'published', start_date: '2026-09-16' },
    { id: '2', title: 'nexus Tech Summit', status: 'published', start_date: '2026-09-14' },
    { id: '3', title: 'AI Founders Workshop', status: 'published', start_date: '2026-08-24' },
  ];

  const eventsToShow = recentEvents && recentEvents.length > 0 ? recentEvents.slice(0, 3) : fallbackEvents;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left Column: Interactive Analytics & Momentum Chart (7 cols) */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
          {/* Header with Timeframe Tabs */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-landing-border/60 pb-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-landing-primary/15 text-landing-primary">
                <Activity className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-landing-text flex items-center gap-2">
                  Platform Activity & Velocity
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Live
                  </span>
                </h4>
                <p className="text-[11px] text-landing-muted">{currentConfig.sublabel}</p>
              </div>
            </div>

            {/* Timeframe pill switches */}
            <div className="inline-flex items-center rounded-xl bg-landing-bg p-1 border border-landing-border/60 text-xs">
              {(['7d', '30d', '12m'] as TimeframeKey[]).map((tf) => (
                <button
                  key={tf}
                  type="button"
                  onClick={() => setTimeframe(tf)}
                  className={`rounded-lg px-2.5 py-1 text-[11px] font-bold transition-all ${
                    timeframe === tf
                      ? 'bg-bg text-landing-text shadow-xs'
                      : 'text-landing-muted hover:text-landing-text'
                  }`}
                >
                  {TIMEFRAME_CONFIG[tf].label.replace('Last ', '').replace('Past ', '')}
                </button>
              ))}
            </div>
          </div>

          {/* Metric Highlights Strip */}
          <div className="grid grid-cols-3 gap-2 rounded-xl bg-landing-bg/40 p-3 border border-landing-border/50">
            <div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-landing-muted block">
                Total Registrations
              </span>
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="text-xl font-black text-landing-text font-display">
                  {loading ? '…' : formatPublicStat(displayTickets)}
                </span>
                <span className="text-[10px] font-bold text-emerald-600 flex items-center">
                  <ArrowUpRight className="h-3 w-3" />
                  {currentConfig.growth}
                </span>
              </div>
            </div>

            <div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-landing-muted block">
                Peak Velocity
              </span>
              <span className="text-xs font-bold text-landing-text mt-1 block">
                {currentConfig.peakRate}
              </span>
            </div>

            <div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-landing-muted block">
                Check-in Speed
              </span>
              <span className="text-xs font-bold text-emerald-700 mt-1 flex items-center gap-1">
                <Zap className="h-3 w-3 text-amber-500 fill-amber-500" />
                {currentConfig.avgScan}
              </span>
            </div>
          </div>

          {/* SVG Sparkline Area Chart */}
          <div className="relative rounded-2xl border border-landing-border/70 bg-gradient-to-b from-white to-landing-bg/30 p-3 pt-4 shadow-inner">
            <svg
              viewBox={`0 0 ${width} ${height}`}
              className="w-full h-36 overflow-visible"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="chartGradientFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#98AA9D" stopOpacity="0.45" />
                  <stop offset="70%" stopColor="#98AA9D" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#98AA9D" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="chartLineGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#697C70" />
                  <stop offset="50%" stopColor="#98AA9D" />
                  <stop offset="100%" stopColor="#5A6B5E" />
                </linearGradient>
              </defs>

              {/* Horizontal Guidelines */}
              <line x1={padX} y1={padY + 20} x2={width - padX} y2={padY + 20} stroke="#E6E2D7" strokeDasharray="3 3" strokeWidth="1" opacity="0.7" />
              <line x1={padX} y1={height / 2} x2={width - padX} y2={height / 2} stroke="#E6E2D7" strokeDasharray="3 3" strokeWidth="1" opacity="0.7" />
              <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#E6E2D7" strokeWidth="1" opacity="0.9" />

              {/* Area & Line */}
              <path d={areaPath} fill="url(#chartGradientFill)" />
              <path
                d={linePath}
                fill="none"
                stroke="url(#chartLineGrad)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {coords.map((pt, idx) => {
                const isHovered = hoveredIndex === idx;
                const isLast = idx === coords.length - 1;
                return (
                  <g key={idx} className="cursor-pointer">
                    {/* Pulsing ring on latest/peak point */}
                    {isLast && (
                      <circle
                        cx={pt.x}
                        cy={pt.y}
                        r="8"
                        fill="#98AA9D"
                        opacity="0.3"
                        className="animate-ping"
                      />
                    )}
                    <circle
                      cx={pt.x}
                      cy={pt.y}
                      r={isHovered ? 6 : 4}
                      fill={isHovered ? '#697C70' : '#FFFFFF'}
                      stroke="#697C70"
                      strokeWidth={isHovered ? 3 : 2}
                      className="transition-all duration-200"
                      onMouseEnter={() => setHoveredIndex(idx)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    />
                    {/* Tooltip on hover */}
                    {isHovered && (
                      <g>
                        <rect
                          x={Math.max(10, Math.min(width - 70, pt.x - 35))}
                          y={pt.y - 32}
                          width="70"
                          height="22"
                          rx="6"
                          fill="#2D3536"
                        />
                        <text
                          x={Math.max(10, Math.min(width - 70, pt.x - 35)) + 35}
                          y={pt.y - 18}
                          fill="#FFFFFF"
                          fontSize="10"
                          fontWeight="bold"
                          textAnchor="middle"
                        >
                          {pt.val} tickets
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* X-Axis labels */}
            <div className="flex justify-between px-3 pt-1 text-[10px] font-semibold text-landing-muted">
              {currentConfig.labels.map((lbl, idx) => (
                <span
                  key={idx}
                  className={`transition-colors ${hoveredIndex === idx ? 'text-landing-text font-bold' : ''}`}
                >
                  {lbl}
                </span>
              ))}
            </div>
          </div>

          {/* Live Activity Pulse Feed */}
          <div className="flex items-center gap-2 rounded-xl bg-landing-bg/50 px-3 py-2 border border-landing-border/60 text-xs">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600">
              <Zap className="h-3 w-3 fill-emerald-500 text-emerald-500" />
            </span>
            <div className="flex-1 truncate text-[11px] text-landing-text">
              <span className="font-bold text-landing-primary">Just now: </span>
              VIP Ticket confirmed for <span className="font-semibold">HackPaglu 2026</span> • Automated QR generated
            </div>
            <span className="text-[10px] font-medium text-landing-muted shrink-0 flex items-center gap-1">
              <Clock className="h-2.5 w-2.5" /> 2m ago
            </span>
          </div>
        </div>

        {/* Right Column: Live & Upcoming Events (5 cols) */}
        <div className="lg:col-span-5 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-landing-border/60 pt-6 lg:pt-0 lg:pl-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-bold text-landing-text">Live & Recent Events</h4>
              <p className="text-[11px] text-landing-muted">Direct attendee pipeline</p>
            </div>
            <span className="rounded-full border border-landing-border bg-landing-bg px-2 py-0.5 text-[10px] font-bold text-landing-text">
              {eventsToShow.length} active
            </span>
          </div>

          {/* Event Cards */}
          <div className="space-y-2.5 flex-1">
            {eventsToShow.map((ev, i) => {
              const capacityPercent = [88, 74, 92][i % 3];
              const registered = [106, 88, 46][i % 3];
              const cap = [120, 120, 50][i % 3];

              return (
                <div
                  key={ev.id ?? ev.title}
                  className="group relative rounded-xl border border-landing-border/70 bg-bg p-3 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-landing-primary/50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-landing-bg border border-landing-border/60 text-landing-primary group-hover:bg-landing-primary group-hover:text-text-inverse transition-colors">
                        {i === 0 ? <Laptop className="h-4 w-4" /> : i === 1 ? <Trophy className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
                      </div>
                      <div className="min-w-0">
                        <h5 className="text-xs font-bold text-landing-text truncate group-hover:text-landing-primary transition-colors">
                          {ev.title ?? 'Untitled Event'}
                        </h5>
                        <p className="text-[10px] text-landing-muted flex items-center gap-1.5 mt-0.5">
                          <span>
                            {ev.start_date
                              ? new Date(ev.start_date).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  timeZone: 'UTC',
                                })
                              : 'Upcoming'}
                          </span>
                          <span>•</span>
                          <span>{i === 0 ? 'Bengaluru' : i === 1 ? 'Hyderabad' : 'Pune'}</span>
                        </p>
                      </div>
                    </div>

                    <span className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 uppercase tracking-wider">
                      <span className="h-1 w-1 rounded-full bg-emerald-500" />
                      Live
                    </span>
                  </div>

                  {/* Capacity Meter Progress Bar */}
                  <div className="mt-2.5 space-y-1">
                    <div className="flex justify-between text-[9px] font-semibold text-landing-muted">
                      <span>Capacity booked</span>
                      <span className="text-landing-text font-bold">{registered}/{cap} ({capacityPercent}%)</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-landing-bg overflow-hidden border border-landing-border/50">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-landing-primary to-landing-secondary transition-all duration-500"
                        style={{ width: `${capacityPercent}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Reassurance Banner */}
          <div className="rounded-xl border border-landing-border/70 bg-landing-bg/50 p-3 text-[11px] text-landing-muted flex items-center gap-2.5">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <div className="leading-tight">
              <span className="font-bold text-landing-text">Instant Settlements: </span>
              Payouts direct to your verified bank account with zero manual delays.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { isAuthenticated, user } = useAuthStore();
  const [publicStats, setPublicStats] = useState<PublicStatsPayload | null>(null);
  const [publicStatsLoading, setPublicStatsLoading] = useState(true);

  useEffect(() => {
    adminApi
      .getPublicStats()
      .then((res) => setPublicStats(res.data?.data ?? null))
      .catch(() => setPublicStats(null))
      .finally(() => setPublicStatsLoading(false));
  }, []);

  const effectiveRole = getEffectiveRole(user);
  const canCreateEvents = userCanCreateEvents(effectiveRole);
  const createEventHref = getCreateEventPath({ isAuthenticated, canCreateEvents });
  const dashboardOrigin = getDashboardOrigin();
  const organizerCtaHref = canCreateEvents
    ? `${dashboardOrigin.replace(/\/$/, '')}${resolveDashboardForUser(user)}`
    : canBecomeOrganizer(user)
      ? '/become-organizer'
      : `${dashboardOrigin.replace(/\/$/, '')}${resolveDashboardForUser(user)}`;
  const organizerCtaLabel = canCreateEvents ? 'Go to dashboard' : 'Become an Organizer';

  const [events, setEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [category, setCategory] = useState('');
  
  const [testimonials, setTestimonials] = useState<OrganizerReview[]>(DEFAULT_TESTIMONIALS);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [homePage, setHomePage] = useState<Page | null>(null);


  // Public listings consume only the real backend events service (GET /api/events).
  // No mock fallback: when the API returns nothing the real empty state renders.
  const displayedEvents = events;

  const dashboardPreviewEvents = useMemo(
    () =>
      events.slice(0, 3).map((ev) => ({
        id: String(ev.id ?? ev.slug ?? ev.title),
        title: ev.title,
        status: ev.status,
        start_date: ev.start_date,
      })),
    [events],
  );

  const renderableHomeSections = useMemo(() => {
    const sections = homePage?.sections ?? [];
    return sections.filter((section) => !HOME_PLACEHOLDER_SECTION_KEYS.has(String(section.key ?? '')));
  }, [homePage?.sections]);



  const isLocalFrontendOnly = typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

  useEffect(() => {
    if (isLocalFrontendOnly) return;
    getPage('home').then(setHomePage).catch(() => {});
  }, [isLocalFrontendOnly]);



  useEffect(() => {
    eventsApi
      .list({
        per_page: 6,
        sortBy: 'created_at',
        sortOrder: 'desc',
        ...(category && { type: category }),
      })
      .then((res) => {
        const data = res.data?.data?.data ?? res.data?.data;
        setEvents(Array.isArray(data) ? data : []);
      })
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false));
  }, [category]);

  useEffect(() => {
    organizerReviewsApi
      .list()
      .then((res) => {
        const data = res.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data);
        }
      })
      .catch(() => {})
      .finally(() => setTestimonialsLoading(false));
  }, []);

  return (
    <MainLayout
      showCreateEvent={false}
      heroHeader={
        <TechHeroSection
          createEventHref={createEventHref}
          realEvents={events}
          ticketCount={publicStats?.total_tickets_sold}
        />
      }
      contentClassName="!pt-0 !px-0 !pb-0 mt-0"
    >
      <div className="relative z-10 overflow-hidden">

      {/* CMS-driven sections (features, testimonials, cta, stats, etc.) */}
      {renderableHomeSections.length > 0 && (
        <SectionsBlock sections={renderableHomeSections} />
      )}

      {/* 2. Event Discovery */}
      <section className="relative overflow-hidden border-t border-landing-border/70 bg-transparent px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-[radial-gradient(circle_at_top,rgba(152,170,157,0.22),transparent_70%)]" />
        <div className={LANDING_SECTION_SHELL_CLASS + ' relative overflow-hidden'}>
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.45),rgba(242,239,226,0.15))]" />
          <div className="pointer-events-none absolute -left-20 top-20 h-64 w-64 rounded-full bg-landing-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-8 h-64 w-64 rounded-full bg-[#B3C9D6]/15 blur-3xl" />

          <motion.div
            className="relative z-10"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] backdrop-blur-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Discover opportunities
              </motion.div>
              <motion.h2
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-5 text-balance text-3xl font-semibold tracking-tight text-landing-text sm:text-4xl lg:text-5xl"
              >
                Discover Events Near You
              </motion.h2>
              <motion.p
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-7 text-landing-muted sm:text-base"
              >
                Find and join events that matter to you with faster filters, sharper discovery, and a cleaner browsing
                experience.
              </motion.p>
            </div>

            <motion.div
              className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              {DISCOVERY_HIGHLIGHTS.map((point) => (
                <DiscoveryFeaturePill key={point.label} point={point} />
              ))}
            </motion.div>

            <motion.div
              className="mt-8 flex flex-wrap justify-center gap-2.5"
              variants={item}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {CATEGORIES.map((cat) => {
                const isActive = category === cat.value;

                return (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-landing-primary/30 ${
                      isActive
                        ? 'border border-landing-primary/20 bg-gradient-to-r from-landing-secondary to-landing-primary text-text-inverse shadow-[0_14px_30px_rgba(105,124,112,0.24)]'
                        : 'border border-white/80 bg-bg/80 text-landing-muted shadow-[0_10px_22px_rgba(45,53,54,0.06)] backdrop-blur-sm hover:border-landing-primary/25 hover:bg-bg hover:text-landing-text hover:shadow-[0_16px_32px_rgba(45,53,54,0.1)]'
                    }`}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </motion.div>

            {eventsLoading ? (
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:gap-6">
                {[1, 2, 3].map((i) => (
                  <DiscoveryLoadingCard key={i} />
                ))}
              </div>
            ) : (
              <motion.div
                className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:gap-6"
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.15 }}
              >
                {displayedEvents.length === 0 ? (
                  <div className="col-span-full py-4 sm:py-8">
                    <motion.div
                      variants={item}
                      className="relative mx-auto max-w-2xl overflow-hidden rounded-[28px] border border-dashed border-landing-border bg-bg/80 px-6 py-10 text-center shadow-[0_16px_40px_rgba(45,53,54,0.06)] backdrop-blur-sm"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-landing-primary/5" />
                      <div className="relative mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-landing-primary/15 bg-landing-primary/10 text-landing-primary shadow-[0_10px_24px_rgba(152,170,157,0.12)]">
                        <SearchX className="h-6 w-6" />
                      </div>
                      <p className="relative text-base font-semibold text-landing-text">No published events yet or no events match your filters.</p>
                      <p className="relative mt-2 text-sm leading-7 text-landing-muted">
                        Try another category or browse the full event list.
                      </p>
                      <Link
                        href="/events"
                        className="relative mt-5 inline-flex items-center gap-2 rounded-2xl border border-landing-primary/20 bg-bg px-5 py-3 text-sm font-semibold text-landing-primary shadow-[0_10px_24px_rgba(45,53,54,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-landing-primary/40 hover:shadow-[0_16px_32px_rgba(45,53,54,0.12)]"
                      >
                        Browse all events <ArrowRight className="h-4 w-4" />
                      </Link>
                    </motion.div>
                  </div>
                ) : (
                  displayedEvents.map((event) => <DiscoveryEventCard key={event.id} event={event} />)
                )}
              </motion.div>
            )}

            <motion.div
              className="mt-8 text-center"
              variants={item}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              <Link
                href="/events"
                className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-5 py-2.5 text-sm font-semibold text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-landing-primary/40 hover:text-landing-secondary hover:shadow-[0_16px_30px_rgba(45,53,54,0.1)]"
              >
                View all events <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 3. Host Events */}
      <section className="relative overflow-hidden border-t border-landing-border/70 bg-transparent px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-44 bg-[radial-gradient(circle_at_top,rgba(152,170,157,0.22),transparent_70%)]" />
        <div className={LANDING_SECTION_SHELL_CLASS + ' relative overflow-hidden'}>
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.45),rgba(242,239,226,0.15))]" />
          <div className="pointer-events-none absolute -left-20 top-16 h-64 w-64 rounded-full bg-landing-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-10 h-64 w-64 rounded-full bg-[#B3C9D6]/15 blur-3xl" />

          <motion.div
            className="relative z-10"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mx-auto max-w-3xl text-center">
              <motion.div
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] backdrop-blur-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Built for organizers
              </motion.div>
              <motion.h2
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-5 text-balance text-3xl font-semibold tracking-tight text-landing-text sm:text-4xl lg:text-5xl"
              >
                Host and Manage Events with Ease
              </motion.h2>
              <motion.p
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-7 text-landing-muted sm:text-base"
              >
                EnthiosHub gives you powerful tools to create events, sell tickets, manage attendees, and track performance.
              </motion.p>
            </div>

            <motion.div
              className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 xl:gap-5"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.18 }}
            >
              {HOST_FEATURES.map((feature) => (
                <HostFeatureCard key={feature.title} feature={feature} />
              ))}
            </motion.div>

            <motion.div
              className="mt-10 text-center"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                href={createEventHref}
                className="group inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-gradient-to-r from-landing-secondary to-landing-primary px-6 py-3 text-sm font-semibold text-text-inverse shadow-[0_16px_32px_rgba(105,124,112,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_42px_rgba(105,124,112,0.3)] active:translate-y-0"
              >
                Start Hosting Events
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 4. How It Works */}
      <section className="relative overflow-hidden border-t border-landing-border/70 bg-transparent px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        {/* Soft centered ambient Eucalyptus glow blob (~250px blur, ~6% opacity) */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[460px] rounded-full filter blur-[220px] opacity-[0.06] bg-[#98AA9D]"
          aria-hidden="true"
        />

        <div className={LANDING_SECTION_SHELL_CLASS + ' relative overflow-hidden'}>
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.45),rgba(242,239,226,0.15))]" />
          <div className="pointer-events-none absolute -left-20 top-20 h-64 w-64 rounded-full bg-landing-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-10 h-64 w-64 rounded-full bg-[#B3C9D6]/15 blur-3xl" />

          <motion.div
            className="relative z-10"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mx-auto max-w-3xl text-center">
              <motion.div
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] backdrop-blur-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Simple process
              </motion.div>
              <motion.h2
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mt-5 text-balance text-3xl font-semibold tracking-tight text-landing-text sm:text-4xl lg:text-5xl"
              >
                How EnthiosHub Works
              </motion.h2>
              <motion.p
                variants={item}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-7 text-landing-muted sm:text-base"
              >
                Launch an event in minutes with a guided flow that keeps setup simple, ticketing clear, and attendee management organized.
              </motion.p>
            </div>

            <div className="relative mt-10">
              {/* Very faint dashed connecting line behind cards (opacity ~9%, Soot #2D3536) */}
              <div
                className="pointer-events-none absolute left-[16.666%] right-[16.666%] top-6 hidden h-[2px] md:block border-t-[1.5px] border-dashed border-[#2D3536] opacity-[0.09]"
                aria-hidden="true"
              />
              <div className="grid gap-5 md:grid-cols-3 xl:gap-6">
                {HOW_IT_WORKS.map((step, i) => (
                  <HowItWorksStepCard key={step.step} step={step} index={i} />
                ))}
              </div>
            </div>

            <motion.div
              className="mt-10 text-center"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                href={createEventHref}
                className="group inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-gradient-to-r from-landing-secondary to-landing-primary px-6 py-3 text-sm font-semibold text-text-inverse shadow-[0_16px_32px_rgba(105,124,112,0.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_42px_rgba(105,124,112,0.3)] active:translate-y-0"
              >
                Start Your First Event
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. Dashboard Preview */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 bg-transparent">
        <div className={LANDING_SECTION_SHELL_CLASS}>
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-landing-text">
              Organizer Dashboard
            </h2>
            <p className="mt-1.5 text-landing-muted text-sm">Everything you need in one place.</p>
          </motion.div>
          <motion.div
            className="landing-box-hover overflow-hidden rounded-[32px] border border-landing-border bg-bg p-5 shadow-[0_20px_60px_rgba(45,53,54,0.12)] sm:p-6"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Simulated macOS / SaaS Browser Toolbar */}
            <div className="flex items-center justify-between border-b border-landing-border/60 bg-landing-bg/50 px-4 py-3 -mx-5 -mt-5 sm:-mx-6 sm:-mt-6 mb-6 rounded-t-[30px]">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-[#FF5F56] border border-black/10 inline-block" />
                <span className="h-3 w-3 rounded-full bg-[#FFBD2E] border border-black/10 inline-block" />
                <span className="h-3 w-3 rounded-full bg-[#27C93F] border border-black/10 inline-block" />
              </div>
              <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-landing-border/80 bg-bg/90 px-4 py-1 text-[11px] font-medium text-landing-muted shadow-2xs">
                <span className="text-[10px]">🔒</span>
                <span>enthioshub.com/dashboard/organizer</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-500/10 px-2.5 py-0.5 rounded-full">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="hidden xs:inline">System Live</span>
              </div>
            </div>

            {/* Metric Tiles with Icons & Micro-Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                {
                  label: 'Events Created',
                  value: formatPublicStat(publicStats?.total_events),
                  icon: CalendarPlus,
                  growth: '+14% this month',
                  accent: 'text-landing-primary bg-landing-primary/10',
                },
                {
                  label: 'Tickets Sold',
                  value: formatPublicStat(publicStats?.total_tickets_sold),
                  icon: Ticket,
                  growth: '+38.2% velocity',
                  accent: 'text-emerald-700 bg-emerald-500/10',
                },
                {
                  label: 'Verified Organizers',
                  value: formatPublicStat(publicStats?.total_organizers),
                  icon: Users,
                  growth: '99.4% active',
                  accent: 'text-soot bg-moss/10',
                },
                {
                  label: 'Active Cities',
                  value: formatPublicStat(publicStats?.active_cities),
                  icon: MapPin,
                  growth: 'Pan-India reach',
                  accent: 'text-amber-700 bg-amber-500/10',
                },
              ].map((stat) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={stat.label}
                    className="group relative overflow-hidden rounded-2xl border border-landing-border/80 bg-gradient-to-br from-white to-landing-bg/40 p-3.5 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-landing-primary/40 hover:shadow-md"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${stat.accent}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                        {stat.growth}
                      </span>
                    </div>
                    <p className="text-xl sm:text-2xl font-black text-landing-text font-display">
                      {publicStatsLoading ? '…' : stat.value}
                    </p>
                    <p className="text-[11px] font-medium text-landing-muted mt-0.5">{stat.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Interactive Mockup Body */}
            <div className="rounded-[24px] border border-landing-border/80 bg-bg p-5 lg:p-6 shadow-sm">
              <DashboardPreviewMockup
                stats={publicStats}
                recentEvents={dashboardPreviewEvents}
                loading={publicStatsLoading || eventsLoading}
              />
            </div>

            {/* Bottom Actions */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
              <Link
                href={organizerCtaHref}
                className="group inline-flex items-center gap-2 rounded-2xl bg-landing-primary px-6 py-3 text-sm font-semibold text-text-inverse shadow-sm transition-all duration-300 hover:bg-landing-primaryHover hover:shadow-md hover:-translate-y-0.5"
              >
                {organizerCtaLabel} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                href="/host-events"
                className="inline-flex items-center gap-2 rounded-2xl border border-landing-border bg-landing-bg/80 px-5 py-3 text-sm font-semibold text-landing-text transition-colors hover:bg-landing-border/50"
              >
                Learn How Hosting Works
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. Platform Features */}
      <section className="relative overflow-hidden border-t border-landing-border/70 bg-transparent px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20">
        {/* Very faint animated grain/noise texture overlay (~4.5% opacity, mix-blend-mode: overlay) */}
        <div
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.045] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='pn'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23pn)'/%3E%3C/svg%3E\")",
          }}
          aria-hidden="true"
        />

        {/* Large soft blurred blob (Mist #B3C9D6, ~260px blur, ~6% opacity) offset to the right side */}
        <div
          className="pointer-events-none absolute right-[10%] top-1/2 -translate-y-1/2 w-[620px] h-[480px] rounded-full filter blur-[240px] opacity-[0.06] bg-[#B3C9D6]"
          aria-hidden="true"
        />

        <div className={LANDING_SECTION_SHELL_CLASS + ' relative overflow-hidden py-10 sm:py-12'}>
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom_right,rgba(255,255,255,0.45),rgba(238,238,234,0.15))]" />
          <div className="pointer-events-none absolute -left-20 top-20 h-64 w-64 rounded-full bg-landing-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-10 h-64 w-64 rounded-full bg-[#B3C9D6]/15 blur-3xl" />

          <motion.div
            className="relative z-10 mx-auto max-w-4xl text-center"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              variants={item}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] backdrop-blur-sm"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Everything you need
            </motion.div>
            <motion.h2
              variants={item}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mt-5 text-balance text-3xl font-semibold tracking-tight text-landing-text sm:text-4xl lg:text-5xl"
            >
              Platform Features
            </motion.h2>
            <motion.p
              variants={item}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-7 text-landing-muted sm:text-base"
            >
              Powerful tools for organizers, teams, and attendees, built to keep event operations secure, fast, and polished.
            </motion.p>
          </motion.div>

          <motion.div
            className="relative mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5 xl:gap-5"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.18 }}
          >
            {PLATFORM_FEATURES.map((feature) => (
              <PlatformFeatureCard key={feature.title} feature={feature} />
            ))}
          </motion.div>

          <motion.div
            className="mt-10 text-center"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              href="/events"
              className="inline-flex items-center gap-2 rounded-full border border-landing-primary/20 bg-bg/80 px-5 py-2.5 text-sm font-semibold text-landing-primary shadow-[0_10px_22px_rgba(45,53,54,0.06)] transition-all duration-300 hover:-translate-y-0.5 hover:border-landing-primary/40 hover:text-landing-secondary hover:shadow-[0_16px_30px_rgba(45,53,54,0.1)]"
            >
              Explore All Features <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 7. Testimonials – TestimonialV2 (3-column vertical scroll, dynamic from API) */}
      <section className="relative overflow-hidden border-t border-landing-border/70 bg-transparent py-6 px-4 sm:px-6 lg:px-8">
        {/* Large soft blurred blob (Moss #697C70 / Eucalyptus, ~260px blur, ~5.5% opacity) behind bottom-right corner */}
        <div
          className="pointer-events-none absolute -right-20 bottom-10 w-[580px] h-[520px] rounded-full filter blur-[250px] opacity-[0.055] bg-[#697C70]"
          aria-hidden="true"
        />

        {/* Large oversized quotation-mark glyph watermark behind the cards cluster (Soot #2D3536, ~4% opacity) */}
        <div
          className="pointer-events-none absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 select-none z-0 overflow-hidden leading-none opacity-[0.04] text-[#2D3536]"
          aria-hidden="true"
          style={{
            fontFamily: "Georgia, Cambria, 'Times New Roman', Times, serif",
            fontSize: 'min(42vw, 460px)',
          }}
        >
          “
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[76rem] py-8">
          <TestimonialV2
            testimonials={testimonials}
            heading="What Organizers Say"
            description="Discover how event organizers use EnthiosHub to run smooth events."
            badge="Testimonials"
            className="bg-transparent"
          />
        </div>
      </section>

      {/* 8. Final CTA – Concert crowd celebration banner */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-transparent">
        <div className={`${SECTION_WIDTH_CLASS} relative overflow-hidden rounded-[24px] sm:rounded-[28px] px-6 py-10 sm:px-10 sm:py-12 lg:py-14 shadow-2xl border border-white/10`}>
          {/* Background Crowd Concert Image */}
          <img
            src="/images/cta-crowd-bg.jpg"
            alt="Event celebration crowd"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />

          {/* Gentle contrast overlay so warm stage lights, crowd, and "It Starts Here" shine through */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/45" />

          {/* Centered Content */}
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-inverse tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                Start Hosting Events Today
              </h2>
              <p className="mt-3 text-text-inverse/95 text-sm sm:text-base max-w-xl mx-auto leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                Join thousands of organizers who use EnthiosHub to create and manage successful events.
              </p>
              <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  href={createEventHref}
                  className="inline-flex items-center justify-center px-6 py-2.5 sm:px-7 sm:py-3 bg-bg text-neutral-900 text-sm font-semibold rounded-full hover:bg-neutral-100 shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                >
                  Create Your First Event
                </Link>
                <Link
                  href="/events"
                  className="inline-flex items-center justify-center px-6 py-2.5 sm:px-7 sm:py-3 border border-white/80 text-text-inverse text-sm font-semibold rounded-full hover:bg-bg/15 hover:border-white transition-all duration-200 backdrop-blur-xs hover:-translate-y-0.5 active:translate-y-0"
                >
                  Explore Events
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      </div>
    </MainLayout>
  );
}
